// HubGrid — Grille filtrée réutilisable pour les 4 hubs
// Migré vers OHGrid v1.1.1 (GROOT UI-002/004/005/006/007)
// Endpoint: /api/metier/hub-grids/grid/{hub_name}
import { useState, useEffect, useCallback, useMemo } from "react";
import { isInstallateurProfile } from "../../utils/tenantProfile";
import { OMV_DOSSIER_COLUMNS } from "../../lib/omvDossier";
// #557 — source UNIQUE du filtrage tri-état, partagée par toutes les grilles.
import { matchTriEtat, matchEtatFacture } from "../../lib/filtreTriEtat";
import {
  CheckCircle2, XCircle, Download, Eye, Archive, ArchiveRestore, Trash2, Package, Send, Plus, FileText,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { useConfigList } from "../../hooks/useConfigList";
import { OHGrid } from "../../components/ui/oh-grid";
import { formatDateFR } from "../../utils/dateFormat";
import { bulkSetSigned, bulkSetBoolField, bulkSetFacturePayee, BulkApplyProduitAmoModal, bulkCreateInvoices, bulkCreateDevis } from "./BulkEditModals";
import GamoraHubBadge from "../../components/governance/GamoraHubBadge";

// hubName HubGrid → hub Gamora (clés alignées sans accent : tous les appelants
// passent "conformite" sans accent, cf. HUB_FILTERS backend ; l'ancienne clé
// "conformité" accentuée ne matchait jamais → badge Gamora muet pour Conformité).
const GAMORA_HUB_MAP = {
  commercial: "commercial",
  operation: "operation",
  conformite: "conformite",
  finance: "finance",
};

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

// Colonnes par hub (spec métier validée) — sortable: true = triable
const HUB_COLUMNS = {
  commercial: [
    { key: "reference", label: "Réf", mono: true, sortable: true },
    { key: "client_nom", label: "Client", sortable: true },
    { key: "ville_cp", label: "Ville - CP", muted: true, sortable: true },
    { key: "pipeline_step", label: "Statut", badge: true, sortable: true },
    { key: "visiteur_amo", label: "Visiteur", sortable: true },
    { key: "contrat_signe", label: "Contrat AMO", check: true },
    { key: "mandat_signe", label: "Mandat", check: true },
    { key: "type_projet", label: "Type projet", muted: true, sortable: true },
    { key: "completude_pct", label: "Complétude", pct: true, sortable: true },
    { key: "prochaine_action", label: "Prochaine action", accent: true },
  ],
  operation: [
    { key: "reference", label: "Réf", mono: true, sortable: true },
    { key: "client_nom", label: "Client", sortable: true },
    { key: "visiteur_amo", label: "Visiteur", sortable: true },
    { key: "installateur_nom", label: "Installateur", sortable: true },
    { key: "type_projet", label: "Type projet", muted: true, sortable: true },
    { key: "has_devis_installateur", label: "Devis inst.", check: true },
    { key: "has_photos", label: "Photos", check: true },
    { key: "date_visite", label: "Date visite", muted: true, sortable: true },
    { key: "statut_chantier", label: "Statut chantier", badge: true, sortable: true },
  ],
  conformite: [
    { key: "reference", label: "Réf", mono: true, sortable: true },
    { key: "client_nom", label: "Client", sortable: true },
    { key: "has_dal", label: "DAL", check: true },
    { key: "has_plan_financement", label: "Plan fin.", check: true },
    { key: "statut_principal", label: "Statut principal", badge: true, sortable: true },
    { key: "date_depot_anah", label: "Date dépôt", muted: true, sortable: true },
    { key: "date_octroi", label: "Date octroi", muted: true, sortable: true },
    { key: "montant_aides_display", label: "Montant aides", money: true, sortable: true },
    { key: "completude_pct", label: "Checklist", pct: true, sortable: true },
    { key: "blocage", label: "Blocage", warning: true },
  ],
  finance: [
    { key: "reference", label: "Réf", mono: true, sortable: true },
    { key: "client_nom", label: "Client", sortable: true },
    { key: "visiteur_amo", label: "Visiteur", sortable: true },
    { key: "visite_payee", label: "Visite payée", check: true },
    { key: "montant_amo", label: "Montant AMO", money: true, sortable: true },
    { key: "reference_facture_amo", label: "Facture AMO" },
    { key: "facture_payee", label: "Facture payée", check: true },
    { key: "date_paiement_display", label: "Date paiement", muted: true, sortable: true },
  ],
  all: [
    { key: "reference", label: "Réf", mono: true, sortable: true },
    { key: "client_nom", label: "Client", sortable: true },
    { key: "pipeline_step", label: "Statut", badge: true, sortable: true },
    { key: "ville_cp", label: "Ville - CP", muted: true, sortable: true },
    { key: "created_at", label: "Date création", muted: true, sortable: true },
    { key: "installateur_nom", label: "Installateur", sortable: true },
    { key: "completude_pct", label: "Complétude", pct: true, sortable: true },
    { key: "prochaine_action", label: "Prochaine action", accent: true },
  ],
};

// ─── Colonnes paramétrables PAR TENANT (refonte Métier/Tenant) ──────────────
// Override de HUB_COLUMNS pour un tenant donné. Gaté tenant : n'affecte QUE les
// tenants listés ; TEF/climat conservent HUB_COLUMNS à l'identique.
// OMV (installateur CEE) : field-names réels (spec SPEC-CEE-OMV-DOSSIER-2026-06-07,
// schéma models.py 6e70d820f). Émis par hub_grids.py uniquement pour omv_france.
// VMC : valeurs métier (technique.type_vmc) → libellés lisibles.
const VMC_LABELS = { simple_flux: "Simple flux", double_flux: "Double flux" };
// OMV_DOSSIER_COLUMNS extrait dans lib/omvDossier.js (logique pure, testée :
// la 1re colonne "N° dossier" pointe sur `reference`, plus sur numero_dossier).
// Cf. AUDIT source réelle numero_dossier 2026-06-12.
const TENANT_HUB_COLUMNS = {
  omv_france: { commercial: OMV_DOSSIER_COLUMNS, all: OMV_DOSSIER_COLUMNS },
};

// Tenant courant — source canonique = localStorage.current_tenant_slug,
// posée par OmniHub.jsx depuis le backend (users.current_tenant), même clé
// que getCurrentTenantSlug()/getAuthHeaders. L'ancienne lecture de
// `currentEntreprise` était cassée : cette clé n'est JAMAIS persistée en
// localStorage (uniquement un useState React dans OmniHub.jsx) → le helper
// retombait toujours sur "tef" et l'override TENANT_HUB_COLUMNS[omv_france]
// ne s'appliquait jamais pour Solène (bug colonnes AMO en grille OMV).
const currentTenantSlug = () => {
  try {
    return localStorage.getItem("current_tenant_slug") || "tef";
  } catch { return "tef"; }
};

const HUB_META = {
  commercial: { label: "Hub Commercial", color: "var(--oh-hub-commercial)" },
  operation: { label: "Hub Opération", color: "var(--oh-hub-operation)" },
  conformite: { label: "Hub Conformité", color: "var(--oh-hub-conformite)" },
  finance: { label: "Hub Finance TEF", color: "var(--oh-hub-finance)" },
  all: { label: "Tous les dossiers", color: "var(--oh-accent, var(--oh-stat-cyan-text))" },
};

// Défauts visuels uniquement — les vraies valeurs viennent de useConfigList (governance)
const DEFAULT_STEP_LABELS = {
  dossier_cree: "Créé",
  visite_planifiee: "Planifié",
  visite_faite: "Visite faite",
  chantier_a_planifier: "Chantier à planifier",
  pret_depot: "Prêt dépôt",
  déposé: "Déposé",
  depose: "Déposé",
  instruction: "Instruction",
  octroi: "Octroi",
  travaux: "Travaux",
  clôture: "Clôturé",
  cloture: "Clôturé",
  refuse: "Refusé",
  abandonne: "Abandonné",
  rejet_abandon: "Rejeté/Abandonné",
};

const STEP_COLORS = {
  dossier_cree: { bg: "bg-slate-500/20", border: "border-[var(--oh-border)]", text: "text-[var(--oh-text-secondary)]" },
  visite_planifiee: { bg: "bg-[var(--oh-accent-bg)]", border: "border-[var(--oh-accent)]", text: "text-[var(--oh-accent-text)]" },
  visite_faite: { bg: "bg-[var(--oh-stat-cyan-bg)]", border: "border-[var(--oh-stat-cyan-border)]", text: "text-[var(--oh-stat-cyan-text)]" },
  chantier_a_planifier: { bg: "bg-[var(--oh-info-bg)]", border: "border-[var(--oh-info-border)]", text: "text-[var(--oh-info-text)]" },
  pret_depot: { bg: "bg-[var(--oh-accent-bg)]", border: "border-[var(--oh-accent)]", text: "text-[var(--oh-accent-text)]" },
  déposé: { bg: "bg-[var(--oh-accent-bg)]", border: "border-[var(--oh-accent)]", text: "text-[var(--oh-accent-text)]" },
  instruction: { bg: "bg-[var(--oh-info-bg)]", border: "border-[var(--oh-info-border)]", text: "text-[var(--oh-info-text)]" },
  octroi: { bg: "bg-[var(--oh-success-bg)]", border: "border-[var(--oh-success-border)]", text: "text-[var(--oh-success-text)]" },
  travaux: { bg: "bg-[var(--oh-info-bg)]", border: "border-[var(--oh-info-border)]", text: "text-[var(--oh-info-text)]" },
  clôture: { bg: "bg-[var(--oh-success-bg)]", border: "border-[var(--oh-success-border)]", text: "text-[var(--oh-success-text)]" },
  refuse: { bg: "bg-[var(--oh-error-bg)]", border: "border-[var(--oh-error-border)]", text: "text-[var(--oh-error-text)]" },
  abandonne: { bg: "bg-[var(--oh-error-bg)]", border: "border-[var(--oh-error-border)]", text: "text-[var(--oh-error-text)]" },
};

// ─── Cell renderers ────────────────────────────────────────────────────────
function CheckCell({ value }) {
  // #557 — TRI-ÉTAT, même doctrine que `residence_principale` (#546) : un `null`
  // affichait la même croix qu'un `false`, et la colonne affirmait « non » là où
  // personne n'avait rien saisi. On ne peut pas filtrer sur trois états et n'en
  // afficher que deux.
  if (value === true) {
    return <CheckCircle2 className="w-4 h-4 text-[var(--oh-success-text)]" />;
  }
  if (value === false) {
    return <XCircle className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />;
  }
  return (
    <span className="text-[11px]" style={{ color: "var(--oh-stat-amber-text)" }}
          title="Non renseigné">—</span>
  );
}

function PctCell({ value }) {
  const pct = value || 0;
  // Couleur sémantique progress : vert OK, ambre warn, rouge danger.
  const pctColor = pct >= 80
    ? "var(--oh-stat-green-text)"
    : pct >= 50 ? "var(--oh-stat-amber-text)" : "var(--oh-stat-red-text)";
  return (
    <div className="flex items-center gap-2">
      <div className="w-12 h-1.5 rounded-full" style={{ background: "var(--oh-border)" }}>
        <div className="h-full rounded-full"
          style={{ width: `${Math.min(pct, 100)}%`, background: pctColor }} />
      </div>
      <span className="text-xs font-medium" style={{ color: pctColor }}>{pct}%</span>
    </div>
  );
}

function BadgeCell({ value, row, stepLabels }) {
  const label = stepLabels[value] || value?.replace(/_/g, " ") || "—";
  const colors = STEP_COLORS[value] || STEP_COLORS.dossier_cree;
  const isRefuse = value === "refuse";
  const anah = row?.statut_anah_label;
  const isAnah = row?.source_statut === "anah";
  const isAmoOverride = row?.source_statut === "amo_override";
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1">
        <Badge className={`text-[10px] px-1.5 py-0 ${colors.bg} ${colors.border} ${colors.text} ${isRefuse ? "font-medium animate-pulse" : ""}`}
          data-testid={`badge-statut-${value}`}>{label}</Badge>
        {isAmoOverride && (
          <Badge className="text-[9px] px-1 py-0 bg-[var(--oh-info-bg)] border-[var(--oh-info-border)] text-[var(--oh-info-text)]"
            data-testid="badge-amo-override">AMO</Badge>
        )}
        {(row?.inactif || row?.actif_inactif === "Inactif") && (
          <Badge className="text-[9px] px-1 py-0"
            style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)", border: "1px solid var(--oh-stat-amber-border)" }}
            data-testid="badge-inactif" title="Dossier inactif (gelé) — dimension orthogonale au statut">Inactif</Badge>
        )}
      </div>
      {isAnah && anah && anah !== label && (
        <span className="text-[9px] leading-tight" style={{ color: "var(--oh-text-muted)" }}>{anah}</span>
      )}
      {isAmoOverride && anah && (
        <span className="text-[9px] leading-tight text-[var(--oh-info-text)]">ANAH: {anah}</span>
      )}
    </div>
  );
}

function AccentCell({ value }) {
  if (!value || value === "—") return <span style={{ color: "var(--oh-text-muted)" }}>—</span>;
  return <span className="text-xs font-medium" style={{ color: "var(--oh-stat-blue-text)" }}>{value}</span>;
}

function WarningCell({ value, row }) {
  const reasons = row?.needs_action_reasons || [];
  const hasAction = row?.needs_action && reasons.length > 0;
  const hasText = value && value.trim();
  if (!hasText && !hasAction) return <span style={{ color: "var(--oh-text-muted)" }}>—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {hasText && <span className="text-xs font-medium" style={{ color: "var(--oh-stat-red-text)" }} data-testid="blocage-text">{value}</span>}
      {hasAction && !hasText && (
        <span className="text-[10px] text-[var(--oh-info-text)]" data-testid="needs-action-indicator"
          title={reasons.map(r => r.label).join("\n")}>
          {reasons.length} action{reasons.length > 1 ? "s" : ""} requise{reasons.length > 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────
export default function HubGrid({ hubName: hubNameProp, onOpenDossier, filterPreset, presets }) {
  // Preset = réglage interne de CETTE même grille (filtre statut HUB_FILTERS backend +
  // colonnes HUB_COLUMNS). On fusionne les ex-5 menus dossiers en 1 grille pilotée par
  // un sélecteur de presets : hubName devient un état local, hubNameProp en valeur
  // initiale. Les presets visibles sont calculés en amont (gating rôle/feature) et
  // passés en prop → aucun preset Finance/Conformité accessible à un rôle non autorisé.
  const [hubName, setHubName] = useState(hubNameProp);
  useEffect(() => { setHubName(hubNameProp); }, [hubNameProp]);
  const showPresets = Array.isArray(presets) && presets.length > 1;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total: 0, step_counts: {}, dossiers: [] });

  // Statuts depuis gouvernance (pas de hardcode)
  const { items: configStatuts } = useConfigList("statuts_dossier");
  const stepLabels = useMemo(() => {
    const merged = { ...DEFAULT_STEP_LABELS };
    configStatuts.forEach(s => { if (s.id && s.label) merged[s.id] = s.label; });
    return merged;
  }, [configStatuts]);

  // Mandataires : source unique = metier_config_listes.mandataires_tef
  // (éditable via Gouvernance > Listes > MetierListesPanel).
  const { items: configMandataires } = useConfigList("mandataires_tef");

  // Statuts OMV : liste éditable per-tenant (metier_config_listes type "statuts_omv",
  // éditée dans Module Aides > Variables et Listes). Champ libre, pas de workflow.
  // Inerte hors omv_france (la liste n'existe pas → []).
  const { items: omvStatuts } = useConfigList("statuts_omv");

  const meta = HUB_META[hubName] || HUB_META.commercial;
  // Colonnes tenant-aware : override par tenant si défini, sinon HUB_COLUMNS (TEF/climat inchangé).
  const tenantSlug = currentTenantSlug();
  const isOmv = isInstallateurProfile();
  // Colonnes : profil installateur (OMV, Climat) → grille CEE (cohérent avec isOmv qui
  // pilote bulk actions + filtres) ; sinon override par slug, sinon défaut (TEF inchangé).
  const _installateurCols = { commercial: OMV_DOSSIER_COLUMNS, all: OMV_DOSSIER_COLUMNS };
  const hubColumns = (isOmv && _installateurCols[hubName])
    || (TENANT_HUB_COLUMNS[tenantSlug] && TENANT_HUB_COLUMNS[tenantSlug][hubName])
    || HUB_COLUMNS[hubName] || HUB_COLUMNS.commercial;

  // Filtres avancés uniformes — déclarés AVANT loadData (anti-TDZ
  // useCallback dependency order, cf. feedback_react_tdz.md).
  const [filterStatutLocal, setFilterStatutLocal] = useState("all");
  const [filterVisiteurLocal, setFilterVisiteurLocal] = useState("all");
  const [showArchivedLocal, setShowArchivedLocal] = useState(false);
  // CC-30 — extension filtres dynamiques (2026-05-07).
  const [filterInstallateurLocal, setFilterInstallateurLocal] = useState("all");
  const [filterMandataireLocal, setFilterMandataireLocal] = useState("all");
  const [filterDeptLocal, setFilterDeptLocal] = useState("all");
  // OMV — filtre Ville dérivé du dataset (entreprise.ville).
  const [filterVilleLocal, setFilterVilleLocal] = useState("all");
  const [filterContratSigneLocal, setFilterContratSigneLocal] = useState("all");  // #557 tri-état
  const [filterVisitePayeeLocal, setFilterVisitePayeeLocal] = useState("all");    // #557 tri-état
  // CC-30 complément (2026-05-07) — 4 filtres bool supplémentaires
  const [filterFacturePayeeLocal, setFilterFacturePayeeLocal] = useState("all");  // #557 tri-état
  const [filterMandatSigneLocal, setFilterMandatSigneLocal] = useState("all");    // #557 tri-état
  const [filterDevisInstLocal, setFilterDevisInstLocal] = useState(false);
  const [filterPhotosLocal, setFilterPhotosLocal] = useState(false);

  // Modal bulk "Appliquer produit AMO" — Hub Finance uniquement (JJ 2026-05-19)
  const [amoModal, setAmoModal] = useState({ open: false, ids: [] });

  // ─── Chargement (useCallback AVANT useEffect — anti-TDZ) ───
  // showArchivedLocal contrôle le query param backend `include_archived`
  // (cf. CC-05 phase b — filtre archive backend rendu conditionnel).
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterPreset) params.append("filter_preset", filterPreset);
      params.append("limit", "500");
      if (showArchivedLocal) params.append("include_archived", "true");
      const url = `${API}/api/metier/hub-grids/grid/${hubName}?${params}`;
      const res = await fetch(url, { headers: getAuthHeaders(), credentials: "include" });
      if (res.ok) {
        const json = await res.json();
        // ID stable pour OHGrid + affichage source_apport dans cellule installateur
        const enriched = (json.dossiers || []).map(d => ({
          ...d,
          id: d.dossier_id || d.référence,
          // Si source_apport=direct → afficher "Apport direct" en lieu de l'installateur null
          installateur_nom: d.source_apport === "direct"
            ? "Apport direct"
            : (d.installateur_nom || ""),
        }));
        // Alerte dev si le dataset est tronqué (tenant > 500 dossiers)
        if (json.total && enriched.length < json.total) {
          console.warn(
            `[HubGrid/${hubName}] Dataset tronqué: ${enriched.length}/${json.total} dossiers chargés. ` +
            `Une pagination serveur sera nécessaire au-delà de 500.`
          );
        }
        setData({ ...json, dossiers: enriched });
      } else toast.error("Erreur chargement grille");
    } catch { toast.error("Erreur réseau"); }
    finally { setLoading(false); }
  }, [hubName, filterPreset, showArchivedLocal]);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Actions unitaires ───
  const handleRowArchive = useCallback(async (row, action) => {
    const endpoint = action === "archive"
      ? `${API}/api/metier/dossiers/${row.dossier_id}/archiver`
      : `${API}/api/metier/dossiers/${row.dossier_id}/desarchiver`;
    try {
      const res = await fetch(endpoint, { method: "POST", headers: getAuthHeaders(), credentials: "include" });
      if (res.ok) {
        toast.success(action === "archive" ? "Dossier archivé" : "Dossier désarchivé");
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur");
      }
    } catch { toast.error("Erreur réseau"); }
  }, [loadData]);

  // ─── Export CSV ───
  const exportCSV = useCallback((rows) => {
    if (!rows || !rows.length) return toast.error("Aucun dossier à exporter");
    const headers = hubColumns.map(c => c.label);
    const csvRows = rows.map(row =>
      hubColumns.map(col => {
        const val = row[col.key];
        if (col.check) return val ? "Oui" : "Non";
        if (col.money) return val ? Number(val).toLocaleString("fr-FR") : "";
        if (col.pct) return `${val || 0}%`;
        if (col.badge) return stepLabels[val] || val || "";
        return val ?? "";
      }).map(v => `"${String(v).replace(/"/g, '""')}"`).join(";")
    );
    const bom = "\uFEFF";
    const csv = bom + [headers.join(";"), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${hubName}_export_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} dossier(s) exporté(s)`);
  }, [hubColumns, stepLabels, hubName]);

  // ─── Config OHGrid ───
  // Stats = step_counts (pipeline steps cliquables)
  const stats = useMemo(() => {
    const entries = Object.entries(data.step_counts || {});
    const total = data.total || 0;
    const hasSteps = entries.length > 0;

    // OMV : KPIs PAR STATUT (dossier) + Total — pour comparer aux KPIs de la grille
    // devis et comprendre les écarts. Comptés sur les dossiers chargés, ordonnés par
    // la liste éditable statuts_omv, puis statuts présents hors liste (prospect…).
    if (isOmv) {
      const counts = {};
      (data.dossiers || []).forEach((d) => { const s = d.statut || "—"; counts[s] = (counts[s] || 0) + 1; });
      const ordered = [];
      (omvStatuts || []).forEach((it) => {
        const lbl = it.label || it.id;
        if (counts[lbl] != null) { ordered.push([lbl, counts[lbl]]); delete counts[lbl]; }
      });
      Object.entries(counts).forEach(([s, c]) => ordered.push([s, c]));
      return [
        { key: "all", label: "Total", value: total, color: "blue", filter: null },
        // Archivés : compteur backend (data.archived_count), TOGGLE-INDÉPENDANT (correct
        // même quand "Inclure archivés" est OFF, donc archives non chargées) — comme
        // les prospects. PAS un filtre sur les lignes chargées.
        { key: "archived", label: "Archivés", value: data.archived_count ?? 0, color: "amber",
          filter: (row) => row.statut === "archive" || row.statut_global === "archive" },
        ...ordered.map(([s, c]) => ({
          key: `omvst_${s}`, label: s, value: c, color: "blue",
          filter: (row) => (row.statut || "—") === s,
        })),
      ];
    }

    // Décompte ventilé (demande JJ 2026-06-16) : la grille affichait des step_counts
    // (« Créé : 38 ») sans expliquer l'écart avec le total (41) = les dossiers
    // archivés (3), masqués par défaut → confusion. On affiche désormais le trio
    // Total / Actifs / Archivés, cohérent (actifs + archivés = total = badge).
    // Compteurs fournis par le backend (toggle-independent) ; un dossier "archivé"
    // porte statut/statut_global == "archive". Repli si vieux backend sans counts.
    const archivedCount = data.archived_count ?? 0;
    const activeCount = data.active_count ?? Math.max(0, total - archivedCount);
    const isArchivedRow = (r) => r?.statut === "archive" || r?.statut_global === "archive";

    // Pour la vue "Tous les dossiers" : Total / Actifs / Archivés cliquables.
    // Pour les autres hubs, pas de préfixe (le total est implicite, seuls les
    // dossiers du hub sont chargés).
    const prefix = hubName === "all" && hasSteps
      ? [
          { key: "all", label: "Total", value: total, color: "blue", filter: null },
          {
            key: "active",
            label: "Actifs",
            value: activeCount,
            color: "green",
            filter: (row) => !isArchivedRow(row),
          },
          // Carte "Archivés" TOUJOURS affichée sur la vue dossiers (même à 0) :
          // elle répond à « combien sont masqués ? » et rend le total lisible
          // (actifs + archivés = total). Cliquer filtre sur les archivés (visibles
          // si le toggle "Inclure archivés" est actif — sinon non chargés).
          {
            key: "archived",
            label: "Archivés",
            value: archivedCount,
            color: "amber",
            filter: (row) => isArchivedRow(row),
          },
        ]
      : [];

    // Afficher jusqu'à 8 étapes pipeline (on a 6 prévus + éventuelles extras)
    const stepStats = entries.slice(0, 8).map(([stepKey, count]) => ({
      key: `step_${stepKey}`,
      label: stepLabels[stepKey] || stepKey,
      value: count,
      color: ["refuse", "abandonne"].includes(stepKey) ? "red" : "blue",
      filter: (row) => row.pipeline_step === stepKey || row.statut === stepKey,
    }));

    return [...prefix, ...stepStats];
  }, [data.step_counts, data.total, data.active_count, data.archived_count, data.dossiers, stepLabels, hubName, isOmv, omvStatuts]);

  // Colonnes OHGrid dynamiques selon hub
  const columns = useMemo(() => hubColumns.map(col => ({
    key: col.key,
    label: col.label,
    sortable: col.sortable !== false,
    render: (value, row) => {
      if (col.check) return <CheckCell value={value} />;
      if (col.pct) return <PctCell value={value} />;
      if (col.badge) return <BadgeCell value={value} row={row} stepLabels={stepLabels} />;
      if (col.accent) return <AccentCell value={value} />;
      if (col.warning) return <WarningCell value={value} row={row} />;
      if (col.money) return value ? `${Number(value).toLocaleString("fr-FR")} €` : "—";
      if (col.vmc) {
        // type_vmc = multi-choix (tableau) ; rétro-compat valeur mono (str).
        const _vmc = Array.isArray(value) ? value : (value ? [value] : []);
        const _txt = _vmc.map((v) => VMC_LABELS[v] || v).join(" + ") || "—";
        return <span style={{ color: "var(--oh-text)" }}>{_txt}</span>;
      }
      if (col.mono) return <span className="font-mono text-xs">{value ?? "—"}</span>;
      // Auto-détection colonnes date — affiche en JJ/MM/AAAA
      if (col.key.startsWith("date_") || col.key === "created_at" || col.key === "updated_at") {
        return <span style={{ color: col.muted ? "var(--oh-text-muted)" : "var(--oh-text)" }}>{formatDateFR(value)}</span>;
      }
      return <span style={{ color: col.muted ? "var(--oh-text-muted)" : "var(--oh-text)" }}>{value ?? "—"}</span>;
    },
  })), [hubColumns, stepLabels]);

  const rowActions = useMemo(() => [
    { label: "Ouvrir", icon: Eye,
      onClick: (row) => onOpenDossier && onOpenDossier(row.dossier_id, hubName) },
    { label: "Archiver", icon: Archive,
      onClick: (row) => handleRowArchive(row, "archive"),
      show: (row) => row.pipeline_step !== "archive" && row.statut !== "archive",
      className: "text-[var(--oh-info-text)]" },
    { label: "Désarchiver", icon: ArchiveRestore,
      onClick: (row) => handleRowArchive(row, "unarchive"),
      show: (row) => row.pipeline_step === "archive" || row.statut === "archive",
      className: "text-[var(--oh-accent-text)]" },
  ], [onOpenDossier, handleRowArchive, hubName]);

  // CC-40 — actions bulk par hub. Le tenantId provient des headers
  // côté request (X-Tenant-ID injecté via getAuthHeaders). En l'absence
  // d'accès direct au tenant ici, on lit localStorage (pattern utilisé
  // ailleurs dans HubGrid).
  const _bulkPatchAndReload = useCallback(async (label, fn, ids) => {
    const tenantId = currentTenantSlug();
    try {
      const n = await fn(ids, tenantId);
      toast.success(`${label} : ${n}/${ids.length} dossier(s) mis à jour`);
      loadData();
    } catch (e) {
      toast.error(`${label} : erreur — ${e.message || e}`);
    }
  }, [loadData]);

  const _bulkArchiveDossiers = useCallback(async (ids, action) => {
    if (!ids.length) return;
    const verb = action === "archive" ? "archiver" : action === "unarchive" ? "désarchiver" : "supprimer";
    if (!window.confirm(`${verb[0].toUpperCase() + verb.slice(1)} ${ids.length} dossier(s) ?`)) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        const url = action === "delete"
          ? `${API}/api/metier/dossiers/${id}`
          : `${API}/api/metier/dossiers/${id}/${action === "archive" ? "archiver" : "desarchiver"}`;
        const res = await fetch(url, {
          method: action === "delete" ? "DELETE" : "POST",
          headers: getAuthHeaders(), credentials: "include",
        });
        if (res.ok) ok += 1; else fail += 1;
      } catch { fail += 1; }
    }
    if (ok) toast.success(`${ok} dossier(s) ${verb}é(s)${fail ? `, ${fail} échec(s)` : ""}`);
    else toast.error(`${verb} : ${fail} échec(s)`);
    loadData();
  }, [loadData]);

  // JJ 2026-05-19 — bulk création factures Pennylane. Séquentiel pour
  // respecter le rate-limit Pennylane. Confirm avant (création externe).
  const _bulkCreateInvoices = useCallback(async (ids) => {
    if (!ids?.length) return;
    if (!window.confirm(`Créer ${ids.length} facture(s) Pennylane (brouillon) ?`)) return;
    const tenantId = currentTenantSlug();
    const toastId = toast.loading(`Création de ${ids.length} facture(s) Pennylane...`);
    try {
      const { success, failed } = await bulkCreateInvoices(ids, tenantId);
      const msg = `${success}/${ids.length} facture(s) créée(s)${failed.length ? ` · ${failed.length} erreur(s)` : ""}`;
      if (failed.length === 0) toast.success(msg, { id: toastId });
      else if (success === 0) toast.error(msg, { id: toastId });
      else toast.warning(msg, { id: toastId });
      if (failed.length && failed.length <= 5) {
        // Affiche les 5 premières erreurs en détail
        failed.forEach(f => console.error(`[bulk-invoice] ${f.id} : ${f.error}`));
      }
      loadData();
    } catch (e) {
      toast.error(`Erreur batch : ${e.message || e}`, { id: toastId });
    }
  }, [loadData]);

  // OMV — bulk "Créer un devis" (Option 1, JJ 2026-06-08). Crée un devis brouillon
  // par dossier et auto-applique la prime CEE du dossier si présente (→ net 0).
  // La prime/délégataire proviennent des clés cachées émises par hub_grids (gaté omv).
  const _bulkCreateDevis = useCallback(async (ids) => {
    if (!ids?.length) return;
    if (!window.confirm(`Créer ${ids.length} devis OMV (brouillon) ?`)) return;
    const tenantId = currentTenantSlug();
    const byId = Object.fromEntries((data.dossiers || []).map(d => [d.dossier_id || d.id, d]));
    const items = ids.map(id => {
      const d = byId[id] || {};
      return { id, montant_prime_cee: d.montant_prime_cee, delegataire_nom: d.delegataire_nom };
    });
    const toastId = toast.loading(`Création de ${ids.length} devis...`);
    try {
      const { success, failed, primed } = await bulkCreateDevis(items, tenantId);
      const primeMsg = primed ? ` · ${primed} avec prime CEE` : "";
      const msg = `${success}/${ids.length} devis créé(s)${primeMsg}${failed.length ? ` · ${failed.length} erreur(s)` : ""}`;
      if (failed.length === 0) toast.success(msg, { id: toastId });
      else if (success === 0) toast.error(msg, { id: toastId });
      else toast.warning(msg, { id: toastId });
      loadData();
    } catch (e) {
      toast.error(`Erreur batch devis : ${e.message || e}`, { id: toastId });
    }
  }, [loadData, data.dossiers]);

  const bulkActions = useMemo(() => [
    // JJ 2026-05-19 — bulk Hub Finance : "Appliquer produit AMO" + "Facturer
    // la sélection (N)". Catalogue fait foi, pas d'input libre.
    // (Export CSV retiré du bulk : déjà présent en row action)
    ...(hubName === "finance" ? [{
      label: "Appliquer produit AMO", icon: Package,
      onClick: (ids) => setAmoModal({ open: true, ids }),
      className: "border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]",
    }, {
      label: "Facturer la sélection", icon: Send,
      onClick: (ids) => _bulkCreateInvoices(ids),
      className: "border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]",
    }] : []),
    // OMV (gating omv_france) — "Créer un devis" : brouillon + auto-prime CEE si présente.
    ...(isOmv ? [{
      label: "Créer un devis", icon: FileText,
      onClick: (ids) => _bulkCreateDevis(ids),
      className: "border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]",
    }] : []),
    // CC-40 — actions bulk AMO (Visite/Facture payée, Contrat AMO, Mandat) : concepts
    // TEF/AMO, masqués pour OMV (gating). TEF/climat inchangés.
    ...(isOmv ? [] : [
    { label: "Cocher Visite payée", icon: CheckCircle2,
      onClick: (ids) => _bulkPatchAndReload(
        "Visite payée", (i, t) => bulkSetBoolField("visite_payee", true, i, t), ids,
      ),
      className: "border-[var(--oh-success-border)] text-[var(--oh-success-text)] hover:bg-[var(--oh-success-bg)]" },
    { label: "Cocher Facture payée", icon: CheckCircle2,
      onClick: (ids) => _bulkPatchAndReload(
        "Facture payée", (i, t) => bulkSetFacturePayee(i, t), ids,
      ),
      className: "border-[var(--oh-success-border)] text-[var(--oh-success-text)] hover:bg-[var(--oh-success-bg)]" },
    { label: "Cocher Contrat AMO", icon: CheckCircle2,
      onClick: (ids) => _bulkPatchAndReload(
        "Contrat AMO", (i, t) => bulkSetSigned("contrat_signe", i, t), ids,
      ),
      className: "border-[var(--oh-stat-cyan-border)] text-[var(--oh-stat-cyan-text)] hover:bg-[var(--oh-stat-cyan-bg)]" },
    { label: "Cocher Mandat", icon: CheckCircle2,
      onClick: (ids) => _bulkPatchAndReload(
        "Mandat", (i, t) => bulkSetSigned("mandat_signe", i, t), ids,
      ),
      className: "border-[var(--oh-stat-cyan-border)] text-[var(--oh-stat-cyan-text)] hover:bg-[var(--oh-stat-cyan-bg)]" },
    ]),
    { label: "Archiver", icon: Archive,
      onClick: (ids) => _bulkArchiveDossiers(ids, "archive"),
      className: "border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]" },
    { label: "Désarchiver", icon: ArchiveRestore,
      onClick: (ids) => _bulkArchiveDossiers(ids, "unarchive"),
      className: "border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]" },
    { label: "Supprimer", icon: Trash2,
      onClick: (ids) => _bulkArchiveDossiers(ids, "delete"),
      className: "border-[var(--oh-error-border)] text-[var(--oh-error-text)] hover:bg-[var(--oh-error-bg)]" },
  ], [hubName, isOmv, _bulkPatchAndReload, _bulkArchiveDossiers, _bulkCreateInvoices, _bulkCreateDevis]);

  const headerActions = useMemo(() => [
    // Refonte P1/HubGrid : bouton "Ajouter un dossier" (manquait pour TEF ET OMV).
    // Navigation via l'event écouté par OmniHub (pas de prop → 0 edit OmniHub.jsx,
    // fichier en périmètre OMV). Visible sur les grilles de dossiers (commercial/all).
    ...(["commercial", "all"].includes(hubName) ? [{
      label: "Ajouter un dossier", icon: Plus, primary: true,
      onClick: () => window.dispatchEvent(new CustomEvent("omnihub:navigate",
        { detail: { hub: "commercial", item: "nouveau-dossier" } })),
      className: "border-[var(--oh-success-border)] text-[var(--oh-success-text)] hover:bg-[var(--oh-success-bg)]",
    }] : []),
    { label: "Export", icon: Download, onClick: () => exportCSV(data.dossiers),
      className: "border-[var(--oh-border)]" },
  ], [hubName, data.dossiers, exportCSV]);

  // (state declared above, before loadData, anti-TDZ)
  const visiteurOptions = useMemo(() => {
    const set = new Set();
    (data.dossiers || []).forEach(d => { if (d.visiteur_amo) set.add(d.visiteur_amo); });
    return Array.from(set).sort().map(v => ({ value: v, label: v }));
  }, [data.dossiers]);

  // CC-30 — options dérivées du dataset chargé (filtres dynamiques).
  const installateurOptions = useMemo(() => {
    const set = new Set();
    (data.dossiers || []).forEach(d => { if (d.installateur_nom) set.add(d.installateur_nom); });
    return Array.from(set).sort().map(v => ({ value: v, label: v }));
  }, [data.dossiers]);

  // Mandataires lus depuis metier_config_listes.mandataires_tef (refactor
  // 2026-05-10 : plus de Set dérivé des dossiers, l'admin gère la liste
  // dans Gouvernance > Listes).
  const mandataireOptions = useMemo(() => {
    return (configMandataires || [])
      .map(m => ({ value: m.id, label: m.label || m.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [configMandataires]);

  const deptOptions = useMemo(() => {
    const set = new Set();
    (data.dossiers || []).forEach(d => {
      // ville_cp typique "75001 Paris" ou "Paris (75)" ; code_postal direct.
      const cp = String(d.code_postal || (d.ville_cp || "").match(/\b(\d{5})\b/)?.[1] || "").trim();
      if (cp.length >= 2) set.add(cp.slice(0, 2));
    });
    return Array.from(set).sort().map(v => ({ value: v, label: `Dépt ${v}` }));
  }, [data.dossiers]);

  // ─── Options OMV (gating omv_france) ───
  const omvStatutOptions = useMemo(() =>
    (omvStatuts || []).map(s => ({ value: s.id ?? s.label, label: s.label ?? s.id })),
    [omvStatuts]);
  const omvVilleOptions = useMemo(() => {
    const set = new Set();
    (data.dossiers || []).forEach(d => { if (d.ville) set.add(d.ville); });
    return Array.from(set).sort().map(v => ({ value: v, label: v }));
  }, [data.dossiers]);
  const omvDeptOptions = useMemo(() => {
    const set = new Set();
    (data.dossiers || []).forEach(d => {
      const cp = String(d.cp || "").trim();
      if (cp.length >= 2) set.add(cp.slice(0, 2));
    });
    return Array.from(set).sort().map(v => ({ value: v, label: `Dépt ${v}` }));
  }, [data.dossiers]);

  // Filtres OMV : Statut (liste éditable) + Ville + Département (CP), + archivés.
  // TEF/climat conservent le set complet ci-dessous (inchangé).
  const omvAdvancedFilters = useMemo(() => [
    { key: "filterStatutLocal", label: "Statut", type: "select",
      defaultValue: filterStatutLocal, options: omvStatutOptions },
    { key: "filterVilleLocal", label: "Ville", type: "select",
      defaultValue: filterVilleLocal, options: omvVilleOptions },
    { key: "filterDeptLocal", label: "Département (CP)", type: "select",
      defaultValue: filterDeptLocal, options: omvDeptOptions },
    { key: "showArchivedLocal", label: "Inclure archivés", type: "checkbox",
      defaultValue: showArchivedLocal },
  ], [filterStatutLocal, omvStatutOptions, filterVilleLocal, omvVilleOptions,
      filterDeptLocal, omvDeptOptions, showArchivedLocal]);

  const tefAdvancedFilters = useMemo(() => [
    { key: "filterStatutLocal", label: "Statut", type: "select",
      defaultValue: filterStatutLocal,
      options: Object.entries(stepLabels).map(([k, lab]) => ({ value: k, label: lab })) },
    { key: "filterVisiteurLocal", label: "Visiteur", type: "select",
      defaultValue: filterVisiteurLocal,
      options: visiteurOptions },
    // CC-30 — 4 selects dynamiques
    { key: "filterInstallateurLocal", label: "Installateur", type: "select",
      defaultValue: filterInstallateurLocal,
      options: installateurOptions },
    { key: "filterMandataireLocal", label: "Mandataire", type: "select",
      defaultValue: filterMandataireLocal,
      options: mandataireOptions },
    { key: "filterDeptLocal", label: "Département", type: "select",
      defaultValue: filterDeptLocal,
      options: deptOptions },
    // 🔑 #557 — TRI-ÉTAT sur les champs qui portent vraiment trois états.
    // Un checkbox ne sait dire que « Oui » : décoché = pas de filtre, jamais
    // « Non ». Or ce qu'on cherche, c'est ce qui MANQUE.
    // ⛔ « Non » et « Non renseigné » restent DISTINCTS : une visite non payée
    // et une visite dont on ignore si elle l'a été n'appellent pas la même
    // action.
    { key: "filterContratSigneLocal", label: "Contrat AMO", type: "tribool",
      defaultValue: filterContratSigneLocal },
    { key: "filterMandatSigneLocal", label: "Mandat", type: "tribool",
      defaultValue: filterMandatSigneLocal },
    { key: "filterVisitePayeeLocal", label: "Visite payée", type: "tribool",
      defaultValue: filterVisitePayeeLocal },
    // Lu sur `facture_payee_etat`, construit par la PROJECTION (#557) :
    // « pas de facture » n'est pas un impayé, et l'écran ne pouvait pas
    // reconstituer la distinction que `bool(…)` avait effacée.
    { key: "filterFacturePayeeLocal", label: "Facture payée", type: "tribool",
      defaultValue: filterFacturePayeeLocal },
    // ⚠️ CES DEUX-LÀ RESTENT DES CHECKBOX, ET C'EST DÉLIBÉRÉ. « Y a-t-il un
    // devis installateur ? », « y a-t-il des photos ? » sont des questions de
    // PRÉSENCE : il n'existe pas d'état « non renseigné ». Leur donner un
    // tri-état afficherait un choix qui ne rendrait jamais rien — un filtre qui
    // ment est pire qu'un filtre incomplet.
    { key: "filterDevisInstLocal", label: "Devis Inst.", type: "checkbox",
      defaultValue: filterDevisInstLocal },
    { key: "filterPhotosLocal", label: "Photos", type: "checkbox",
      defaultValue: filterPhotosLocal },
    { key: "showArchivedLocal", label: "Inclure archivés", type: "checkbox",
      defaultValue: showArchivedLocal },
  ], [
    filterStatutLocal, filterVisiteurLocal, visiteurOptions, showArchivedLocal,
    stepLabels, filterInstallateurLocal, installateurOptions,
    filterMandataireLocal, mandataireOptions,
    filterDeptLocal, deptOptions, filterContratSigneLocal,
    filterVisitePayeeLocal, filterFacturePayeeLocal, filterMandatSigneLocal,
    filterDevisInstLocal, filterPhotosLocal,
  ]);

  // Gating omv_france : jeu de filtres réduit OMV, sinon set TEF/climat complet.
  const advancedFilters = isOmv ? omvAdvancedFilters : tefAdvancedFilters;

  const handleAdvFilter = useCallback(({ key, value }) => {
    if (key === "filterStatutLocal") setFilterStatutLocal(value);
    if (key === "filterVisiteurLocal") setFilterVisiteurLocal(value);
    if (key === "showArchivedLocal") setShowArchivedLocal(value);
    if (key === "filterInstallateurLocal") setFilterInstallateurLocal(value);
    if (key === "filterMandataireLocal") setFilterMandataireLocal(value);
    if (key === "filterDeptLocal") setFilterDeptLocal(value);
    if (key === "filterVilleLocal") setFilterVilleLocal(value);
    if (key === "filterContratSigneLocal") setFilterContratSigneLocal(value);
    if (key === "filterVisitePayeeLocal") setFilterVisitePayeeLocal(value);
    if (key === "filterFacturePayeeLocal") setFilterFacturePayeeLocal(value);
    if (key === "filterMandatSigneLocal") setFilterMandatSigneLocal(value);
    if (key === "filterDevisInstLocal") setFilterDevisInstLocal(value);
    if (key === "filterPhotosLocal") setFilterPhotosLocal(value);
  }, []);

  const filteredDossiers = useMemo(() => {
    return (data.dossiers || []).filter(d => {
      if (!showArchivedLocal && (d.statut === "archive" || d.statut_global === "archive")) return false;
      if (filterStatutLocal !== "all" && d.pipeline_step !== filterStatutLocal && d.statut !== filterStatutLocal) return false;
      if (filterVisiteurLocal !== "all" && d.visiteur_amo !== filterVisiteurLocal) return false;
      // CC-30 — 4 filtres dynamiques + 2 booléens
      if (filterInstallateurLocal !== "all" && d.installateur_nom !== filterInstallateurLocal) return false;
      if (filterMandataireLocal !== "all" && d.mandataire !== filterMandataireLocal) return false;
      // OMV — filtre Ville (entreprise.ville).
      if (filterVilleLocal !== "all" && d.ville !== filterVilleLocal) return false;
      if (filterDeptLocal !== "all") {
        // OMV : CP plat (d.cp) ; TEF : code_postal / ville_cp.
        const cp = String(d.cp || d.code_postal || (d.ville_cp || "").match(/\b(\d{5})\b/)?.[1] || "");
        if (cp.slice(0, 2) !== filterDeptLocal) return false;
      }
      // 🔑 #557 — TRI-ÉTAT. ⛔ Plus de `if (filtre && !d.champ)` : `!valeur` est
      // vrai pour `false`, pour `null` ET pour `undefined` — c'est la classe
      // falsy, et elle rendait « non » et « non renseigné » indiscernables.
      if (!matchTriEtat(filterContratSigneLocal, d.contrat_signe)) return false;
      if (!matchTriEtat(filterMandatSigneLocal, d.mandat_signe)) return false;
      if (!matchTriEtat(filterVisitePayeeLocal, d.visite_payee)) return false;
      // Lu sur l'état construit par la projection, pas sur le booléen aplati.
      if (!matchEtatFacture(filterFacturePayeeLocal, d)) return false;
      // Champs de PRÉSENCE — 2 états assumés (cf. déclaration des filtres).
      if (filterDevisInstLocal && !d.has_devis_installateur) return false;
      if (filterPhotosLocal && !d.has_photos) return false;
      return true;
    });
  }, [
    data.dossiers, showArchivedLocal, filterStatutLocal, filterVisiteurLocal,
    filterInstallateurLocal, filterMandataireLocal, filterVilleLocal,
    filterDeptLocal, filterContratSigneLocal, filterVisitePayeeLocal,
    filterFacturePayeeLocal, filterMandatSigneLocal,
    filterDevisInstLocal, filterPhotosLocal,
  ]);

  const searchFilter = useCallback((row, q) => (
    (row.référence || row.reference || "").toLowerCase().includes(q) ||
    (row.client_nom || "").toLowerCase().includes(q) ||
    (row.ville_cp || "").toLowerCase().includes(q) ||
    (row.visiteur_amo || "").toLowerCase().includes(q) ||
    (row.installateur_nom || "").toLowerCase().includes(q) ||
    // OMV : retrouver un dossier par établissement (site travaux) ou par
    // SIRET/SIREN. siret.includes(q) couvre aussi le SIREN (préfixe du SIRET) ;
    // siren explicite pour la saisie 9 ch. Inerte si le row n'a pas ces clés (TEF).
    (row.nom_etablissement || "").toLowerCase().includes(q) ||
    (row.siret || "").includes(q) ||
    (row.siren || "").includes(q)
  ), []);

  return (
    <div className="space-y-4" data-testid={`hub-grid-${hubName}`}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-1 h-6 rounded-full" style={{ background: meta.color }} />
        <h2 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>{meta.label}</h2>
        <Badge className="text-xs px-2 py-0.5"
          style={{ background: `${meta.color}20`, color: meta.color, border: `1px solid ${meta.color}40` }}>
          {data.total} dossier{data.total > 1 ? "s" : ""}
        </Badge>
        {GAMORA_HUB_MAP[hubName] && (
          <GamoraHubBadge hub={GAMORA_HUB_MAP[hubName]} label={meta.label} />
        )}
      </div>

      {/* Sélecteur de presets — fusion des ex-5 menus dossiers en 1 grille.
          Chaque preset = un réglage (filtre statut + colonnes) de CETTE grille.
          La liste presets est gatée en amont (rôle/feature) → un rôle non
          autorisé ne reçoit jamais le preset Finance/Conformité. */}
      {showPresets && (
        <div className="flex items-center gap-2 flex-wrap" role="tablist" aria-label="Vues des dossiers" data-testid="hub-grid-presets">
          {presets.map((p) => {
            const active = p.key === hubName;
            return (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`hub-preset-${p.key}`}
                onClick={() => setHubName(p.key)}
                className="text-sm px-3 py-1.5 rounded-lg border transition-colors"
                style={active
                  ? { background: `${meta.color}20`, color: meta.color, borderColor: `${meta.color}55` }
                  : { background: "var(--oh-bg-surface)", color: "var(--oh-text)", borderColor: "var(--oh-border)" }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      )}

      <OHGrid
        data={filteredDossiers}
        columns={columns}
        stats={stats}
        rowActions={rowActions}
        bulkActions={bulkActions}
        headerActions={headerActions}
        advancedFilters={advancedFilters}
        onAdvancedFilter={handleAdvFilter}
        selectable={true}
        pagination={true}
        pageSize={25}
        loading={loading}
        onRefresh={loadData}
        onRowClick={(row) => onOpenDossier && onOpenDossier(row.dossier_id, hubName)}
        searchPlaceholder="Rechercher par réf, client, ville, SIRET, établissement..."
        searchFilter={searchFilter}
        defaultSortKey="created_at"
        defaultSortDir="desc"
        emptyMessage="Aucun dossier dans ce hub"
        testIdPrefix={`hub-${hubName}`}
      />

      {/* Modal bulk "Appliquer produit AMO" — Hub Finance uniquement (JJ 2026-05-19) */}
      <BulkApplyProduitAmoModal
        open={amoModal.open}
        onClose={() => setAmoModal({ open: false, ids: [] })}
        selectedIds={amoModal.ids}
        tenantId={currentTenantSlug()}
        onDone={loadData}
      />
    </div>
  );
}
